import { api } from '@/utils/api';
import { Button, Flex, FormControl, FormErrorMessage, Heading, Input } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextareaCounter } from '@sphere/ui';
import useTranslation from 'next-translate/useTranslation';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { SupportFormSchemaType } from './SupportForm.types';
import { supportValidationSchema } from './SupportForm.utils';

export const SupportForm = () => {
  const { t } = useTranslation('support');
  const createSupportTicket = api.support.createSupportTicket.useMutation();

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors, isSubmitting },
  } = useForm<SupportFormSchemaType>({
    mode: 'onBlur',
    resolver: zodResolver(supportValidationSchema(t)),
  });

  const onSubmit: SubmitHandler<SupportFormSchemaType> = async ({ name, email, message }) => {
    try {
      await createSupportTicket.mutateAsync({ name, email, message });
      toast.success(t('form.toast.success'));
      resetForm();
    } catch {
      toast.error(t('form.toast.error'));
    }
  };

  return (
    <>
      <Heading
        as="h2"
        fontSize={{ base: 'md', lg: '2xl' }}
        fontWeight="medium"
        mb={{ base: 'space.24', lg: 'space.40' }}
      >
        {t('form.title')}
      </Heading>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Flex flexDir={{ base: 'column', lg: 'row' }} gap="space.24" mb="space.24">
          <FormControl isInvalid={!!errors.name}>
            <Input
              bg="gray.2"
              placeholder={t('form.inputs.name.placeholder')}
              {...register('name')}
            />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors.email}>
            <Input
              type="email"
              bg="gray.2"
              placeholder={t('form.inputs.email.placeholder')}
              {...register('email')}
            />
            <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
          </FormControl>
        </Flex>
        <FormControl isInvalid={!!errors.message}>
          <TextareaCounter
            bg="gray.2"
            placeholder={t('form.inputs.message.placeholder')}
            rows={6}
            maxLength={1000}
            {...register('message')}
          />
          <FormErrorMessage>{errors.message?.message}</FormErrorMessage>
        </FormControl>
        <Button mt={{ base: 'space.24', lg: 'space.40' }} type="submit" isLoading={isSubmitting}>
          {t('form.submit')}
        </Button>
      </form>
    </>
  );
};
